/**
 * The onboarding files board — attention first (walk 4, #2267; first built in phase 530, #1939).
 *
 * <p>One page a staffer opens in the morning to see what needs THEM. The approved design (Carlos,
 * 24 Sep — the "Files board: attention first" screen) groups the open files in two:</p>
 * <ul>
 *   <li><b>Needs you</b> — files with an open item whose resolving action this viewer may take.
 *       The ENGINE decides that per viewer and sends it as `attention: "needs_you"`; the page never
 *       re-derives a role. Each row says the one next step in plain words and when it is due.</li>
 *   <li><b>Waiting on others</b> — the client, a person, the checks. Nothing to do yet; the row
 *       says who, for what, since when and when it is due.</li>
 * </ul>
 * <p>Completed files and cancelled files are hidden by default, each behind a link that shows them
 * (design assumption 5, adopted in the contract). A cancelled file is one the platform CANCELLED —
 * terminal with the job state `cancelled` — never a guess from its name. The design also hides test
 * files, but the platform holds no test marker on a job or a tenant, so nothing is hidden as a
 * test: a real client whose name happens to contain "test" must never drop off the board.</p>
 *
 * <h3>Degrading against an older engine</h3>
 * <p>`attention` and `next_step` are walk-4 additions. An engine that does not send them still
 * gets a working board: a file is "needs you" when its declared stage owner is `staff` (the rule
 * the pre-walk-4 "Needs staff" filter used), and the next step is the declared `next_action`.</p>
 *
 * <h3>What the page still refuses to do (phase 530)</h3>
 * <ul>
 *   <li><b>No job id in anything a staffer reads</b> (AC-5). The id is in each file link's `href`
 *       — a routing key — and nowhere else. The test suite walks the rendered tree for one.</li>
 *   <li><b>No invented name.</b> `client_name: null` reads "no client name recorded".</li>
 *   <li><b>No stage dressed as English it does not have</b>: an unnamed stage shows the raw state
 *       plainly and says so (AC-6).</li>
 *   <li><b>No silent truncation</b>: the board pages by cursor (#2508), and "Show more files" is there whenever the engine has more.</li>
 *   <li><b>No re-sorting</b>: within each group, rows keep the engine's order.</li>
 * </ul>
 *
 * <p>Design tokens only — no raw hex (test/polish/hex-fence.test.ts). (No client's name appears in
 * this bundle: one portal serves every tenant — fence `engine-client-logic`, D4.)</p>
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  Icon,
  LastUpdated,
  PageHeader,
  Pill,
  StatusBadge,
  Table,
  type Column,
} from "@ardept/design-system";
import { ApiError } from "../api/fetcher.js";
import { useClientTenant, useFamilies, useJobStatusFiles } from "../api/portalQueries.js";
import type { JobStatusAttention, JobStatusFile } from "../api/types.js";
import {
  ONBOARDING_LABEL,
  boardFor,
  defaultBoard,
  type BoardEntry,
} from "../nav/familyRegistry.js";
import { usePageTitle } from "../nav/usePageTitle.js";
import { arrivedVia } from "./arrivedVia.js";
import { dueLine, openedLabel, sinceLabel } from "./dates.js";
import "./FilesBoardPage.css";

// ── The declared `owner` vocabulary, in English ──────────────────────────────────────────
//
// `status_view.stages.<STATE>.owner` is a CLOSED schema enum (packages/workflow-schema v0.json:
// client | staff | party | system | none). The words are the portal's, the values are the
// platform's, and this is the single place the two meet. The file page and the chase list import
// {@link ownerLabel} from here rather than re-spelling it.

/** The declared owner value that means the firm itself is holding the file. */
export const OWNER_STAFF = "staff";

/** Who a file is waiting on, in the second person a staffer reads it in. */
export const OWNER_COPY: Readonly<Record<string, string>> = {
  client: "the client",
  staff: "your team",
  party: "a party",
  system: "the platform",
  none: "nobody",
};

/**
 * The English for a declared owner, or the raw token when the platform grows a sixth value — the
 * print-it-ugly rule. `null` (no declared owner) yields `null`: the row says nothing.
 */
export function ownerLabel(owner: string | null): string | null {
  if (owner === null) return null;
  return OWNER_COPY[owner] ?? owner;
}

/** "The client" rather than "the client" — the same words, at the start of a cell. */
function capitalise(text: string): string {
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);
}

// ── Copy this page owns ──────────────────────────────────────────────────────────────────

/** What a row shows when the declared display-name path resolves to nothing (AC-5). */
export const NO_CLIENT_NAME = "no client name recorded";

/** What a cell shows when the platform holds no value at all for it. */
const NOT_RECORDED = "not recorded";

/** The marker on a stage the declaration has not named (AC-6). */
const STAGE_NOT_NAMED = "not named yet";

// ── Days open (kept for the file page and the tests) ────────────────────────────────────

/** A day, in milliseconds. */
const DAY_MS = 86_400_000;

/**
 * How long a file has been open, DERIVED at render from `opened_at` against the page's own clock.
 * The engine deliberately sends the instant, not an integer that would go stale in a cache.
 * Returns `null` for an unparseable instant rather than `NaN` days.
 */
export function daysOpen(openedAt: string, now: number): number | null {
  const opened = new Date(openedAt).getTime();
  if (Number.isNaN(opened)) return null;
  return Math.max(0, Math.floor((now - opened) / DAY_MS));
}

/** "today" / "1 day" / "12 days" — what a person says, not a duration format. */
export function daysOpenLabel(days: number | null): string {
  if (days === null) return NOT_RECORDED;
  if (days === 0) return "today";
  return days === 1 ? "1 day" : `${days} days`;
}

// ── Grouping ─────────────────────────────────────────────────────────────────────────────

/**
 * Which group a file is in. A PARTITION: every file lands in exactly one, tested in this order —
 * hidden (cancelled) first, then finished, then the viewer's attention.
 */
export type BoardGroup = "needs-you" | "waiting" | "completed" | "hidden";

/**
 * The file's attention for this viewer: the engine's walk-4 answer when it sent one, else the
 * pre-walk-4 rule (a staff-owned stage needs the firm).
 */
export function attentionOf(file: JobStatusFile): JobStatusAttention {
  if (file.attention !== undefined) return file.attention;
  if (file.is_terminal) return "done";
  return file.owner === OWNER_STAFF ? "needs_you" : "waiting_on_others";
}

/**
 * The job state the platform sets when a file is cancelled (`IJobStore.CancelAsync`: state
 * `cancelled`, `is_terminal` true; `OperatorJobStatus.Cancelled`). A platform word, not a tenant's —
 * no declared stage label or state name is read, so a workflow state that merely mentions a
 * cancellation is never mistaken for one.
 */
export const CANCELLED_STATE = "cancelled";

/**
 * Why a file is hidden by default, or null. Only one reason exists: the platform cancelled it.
 *
 * <p>There is deliberately NO "test file" reason. The platform carries no test or synthetic
 * marker on a job or a tenant, and the earlier guess — a client name containing the word "test" —
 * would hide a real client. Hidden is never silent: the footer link says how many, and shows
 * them.</p>
 */
export function hiddenReason(file: JobStatusFile): "cancelled" | null {
  if (file.is_terminal && file.state === CANCELLED_STATE) return "cancelled";
  return null;
}

/** The group a file is in. The whole rule, in one place, read by page and test alike. */
export function groupOf(file: JobStatusFile): BoardGroup {
  if (hiddenReason(file) !== null) return "hidden";
  if (file.is_terminal) return "completed";
  return attentionOf(file) === "needs_you" ? "needs-you" : "waiting";
}

/** The file's one next step: the engine's walk-4 words, else the declared next action. */
export function nextStepOf(file: JobStatusFile): { text: string | null; due: string | null } {
  if (file.next_step !== undefined) return { text: file.next_step.text, due: file.next_step.due };
  return { text: file.next_action, due: null };
}

// ── The URL ──────────────────────────────────────────────────────────────────────────────

/**
 * The query parameter the "show" choices live in, so a staffer can bookmark "with completed
 * files" and paste the same board to a colleague. Tokens: `completed`, `hidden` (the cancelled
 * files; the token predates the test-file heuristic's removal and is kept so bookmarks still work).
 */
const SHOW_PARAM = "show";

export type ShowChoice = "completed" | "hidden";

const SHOW_ORDER: readonly ShowChoice[] = ["completed", "hidden"];

/** The choices a URL carries. Unknown tokens (an old bookmark's `active`) are dropped. */
export function parseShow(raw: string | null): ShowChoice[] {
  if (raw === null) return [];
  const wanted = new Set(raw.split(",").map((token) => token.trim()));
  return SHOW_ORDER.filter((choice) => wanted.has(choice));
}

// ── Pieces ───────────────────────────────────────────────────────────────────────────────

/**
 * The file's name as the link into its detail. The `href` is the ONE place a job id appears.
 * `base` is the board's own address (#2508: `/onboarding`, or `/boards/<workflow>`), so a file
 * opens under the board it was listed on.
 */
function FileLink({ file, base }: { file: JobStatusFile; base: string }): JSX.Element {
  const named = file.client_name !== null && file.client_name.trim() !== "";
  return (
    <Link
      className={`fb-file${named ? "" : " fb-file--unnamed"}`}
      to={`${base}/${file.job_id}`}
    >
      {named ? file.client_name : NO_CLIENT_NAME}
    </Link>
  );
}

/** The stage, in the tenant's words — or plainly, when the declaration has none for it. */
function StageValue({ file }: { file: JobStatusFile }): JSX.Element {
  if (file.stage_label_source === "state_id") {
    return (
      <span className="fb-stage fb-stage--raw">
        <code className="fb-stage__state">{file.stage_label}</code>
        <span className="fb-stage__note">{STAGE_NOT_NAMED}</span>
      </span>
    );
  }
  return (
    <span className="fb-stage">
      <StatusBadge status={file.stage_label} />
    </span>
  );
}

/** A due date line, or nothing. */
function Due({ iso, now }: { iso: string | null; now: number }): JSX.Element | null {
  const due = dueLine(iso, now);
  if (due === null) return null;
  return <span className={`fb-due${due.overdue ? " fb-due--overdue" : ""}`}>{due.text}</span>;
}

/**
 * One "Needs you" row: the file, where it is, the one next step with its due date, and the way in.
 *
 * <p>The row's button opens the FILE, where the action itself lives: a board row carries no action
 * reference in the contract, so the file page — which does — is where the button that acts is
 * drawn, and drawn only for a viewer allowed to press it.</p>
 */
function NeedsYouRow({ file, now, base }: { file: JobStatusFile; now: number; base: string }): JSX.Element {
  const step = nextStepOf(file);
  const opened = openedLabel(file.opened_at, now);
  const stageFor = sinceLabel(file.stage_since, now);
  const via = arrivedVia(file.opened_via);
  return (
    <article className="fb-card" data-group="needs-you">
      <div className="fb-card__who">
        <FileLink file={file} base={base} />
        <span className="fb-card__meta">
          {opened ?? NOT_RECORDED}
          {/* Card #2909 — how it arrived, in plain words (never a raw `platform:` code). */}
          {via !== null && ` · arrived via ${via}`}
        </span>
      </div>
      <div className="fb-card__stage">
        <StageValue file={file} />
        {stageFor !== null && (
          <span className="fb-card__meta">
            {stageFor === "today" ? "At this stage since today" : `At this stage for ${stageFor}`}
          </span>
        )}
      </div>
      <div className="fb-card__next">
        <span className="fb-card__step">{step.text ?? "Open the file to see what is left to do"}</span>
        <Due iso={step.due} now={now} />
      </div>
      <Link className="btn btn--primary fb-card__go" to={`${base}/${file.job_id}`}>
        Open file
      </Link>
    </article>
  );
}

/** One row of a board table. `id` is the design-system `Table`'s key — never rendered. */
interface BoardRow {
  id: string;
  file: JobStatusFile;
}

/** The "Waiting on others" table: who, for what, since when, due when. */
function waitingColumns(now: number, base: string): Column<BoardRow>[] {
  return [
    { key: "file", label: "Client", render: (row) => <FileLink file={row.file} base={base} /> },
    {
      key: "who",
      label: "Waiting on",
      render: (row) => {
        const who = ownerLabel(row.file.owner);
        return who === null ? (
          <span className="fb-none">{NOT_RECORDED}</span>
        ) : (
          // The platform's own checks are under way; anyone else is being waited on.
          <Pill tone={row.file.owner === "system" ? "progress" : "waiting"}>{capitalise(who)}</Pill>
        );
      },
    },
    {
      key: "for",
      label: "For",
      render: (row) => {
        const step = nextStepOf(row.file);
        return step.text === null ? (
          <span className="fb-none">{NOT_RECORDED}</span>
        ) : (
          <span className="fb-muted">{step.text}</span>
        );
      },
    },
    {
      key: "since",
      label: "Since",
      render: (row) => {
        const since = sinceLabel(row.file.stage_since, now);
        return <span className="fb-muted fb-days">{since === null ? NOT_RECORDED : capitalise(since)}</span>;
      },
    },
    {
      key: "due",
      label: "Due",
      render: (row) => {
        const step = nextStepOf(row.file);
        return step.due === null ? (
          <span className="fb-muted">No due date</span>
        ) : (
          <Due iso={step.due} now={now} />
        );
      },
    },
  ];
}

/**
 * #2507 — what a finished file still owes, in words: "3 still owed" on an approved file that is
 * chased after it closes, "Nothing owed" when that count is 0, and "Not chased" for a finished file
 * the engine sends no count for (declined, cancelled: D10 — they are never chased).
 */
export function owedLabel(file: JobStatusFile): { text: string; owed: boolean } {
  if (file.owed_count === undefined) return { text: "Not chased", owed: false };
  if (file.owed_count <= 0) return { text: "Nothing owed", owed: false };
  return { text: `${file.owed_count} still owed`, owed: true };
}

/** The completed files: the file, its final stage, what it still owes, when it last moved. */
function completedColumns(now: number, base: string): Column<BoardRow>[] {
  return [
    { key: "file", label: "Client", render: (row) => <FileLink file={row.file} base={base} /> },
    { key: "stage", label: "Stage", render: (row) => <StageValue file={row.file} /> },
    {
      key: "owed",
      label: "Still owed",
      render: (row) => {
        const owed = owedLabel(row.file);
        return owed.owed ? <Pill tone="attention">{owed.text}</Pill> : <span className="fb-muted">{owed.text}</span>;
      },
    },
    {
      key: "when",
      label: "Last moved",
      render: (row) => {
        const since = sinceLabel(row.file.updated_at, now);
        return (
          <span className="fb-muted">
            {since === null ? NOT_RECORDED : since === "today" ? "Today" : `${since} ago`}
          </span>
        );
      },
    },
  ];
}

/** The cancelled files: the file, why it is hidden, and its stage. */
function hiddenColumns(base: string): Column<BoardRow>[] {
  return [
    { key: "file", label: "Client", render: (row) => <FileLink file={row.file} base={base} /> },
    {
      key: "why",
      label: "Hidden because",
      render: () => <Pill tone="muted">Cancelled</Pill>,
    },
    { key: "stage", label: "Stage", render: (row) => <StageValue file={row.file} /> },
  ];
}

// ── "Onboarding isn't set up" (walk 3) ─────────────────────────────────────────────────

/** The engine's error code for a board asked of a tenant that declares no onboarding spine. */
const NO_SPINE_ERROR_CODE = "invalid_workflow_id";

/**
 * Whether a failed board read is the engine saying "this tenant has no onboarding" (400
 * `invalid_workflow_id`), rather than a real failure.
 */
export function isOnboardingNotSetUp(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 400) return false;
  try {
    const body = JSON.parse(error.bodyText) as { error_code?: unknown };
    return body.error_code === NO_SPINE_ERROR_CODE;
  } catch {
    return false;
  }
}

/** The not-set-up sentence, naming the tenant when `/me` has named it. */
export function notSetUpTitle(tenantName: string | null, label: string = ONBOARDING_LABEL): string {
  return `${label} isn't set up for ${tenantName ?? "this account"}.`;
}

/**
 * #2508 — which board an address means. No workflow (`/onboarding`) is the tenant's DEFAULT board;
 * `/boards/<id>` is that workflow's board. `undefined` while the families read is in flight or
 * when the tenant has no such board.
 */
export function resolveBoard(
  families: Parameters<typeof boardFor>[0],
  workflowId: string | undefined,
): BoardEntry | undefined {
  return workflowId === undefined ? defaultBoard(families) : boardFor(families, workflowId);
}

// ── The page ─────────────────────────────────────────────────────────────────────────────

export function FilesBoardPage({ workflowId }: { workflowId?: string } = {}): JSX.Element {
  const identity = useClientTenant();
  const tenantId = identity.tenantId;

  const [searchParams, setSearchParams] = useSearchParams();
  const show = parseShow(searchParams.get(SHOW_PARAM));
  const showCompleted = show.includes("completed");
  const showHidden = show.includes("hidden");
  const [query, setQuery] = useState("");

  // Finished files are a REQUEST parameter: the engine excludes them by default, so neither the
  // completed group nor a cancelled file can be shown unless they were asked for.
  const includeTerminal = showCompleted || showHidden;

  // Walk 3: the families read is the ONE "has onboarding" signal. The board read waits for it and
  // is never sent when it says NO. A FAILED families read does not hold the board hostage.
  //
  // #2508: the families read also says WHICH board this address means — its workflow (sent on every
  // read), its label and its address. A failed families read falls back to the address's own
  // workflow (or none, for `/onboarding`), so the engine still answers.
  const families = useFamilies(tenantId);
  const boardEntry = resolveBoard(families.data?.families, workflowId);
  const label = boardEntry?.label ?? ONBOARDING_LABEL;
  const base = boardEntry?.route ?? (workflowId === undefined ? "/onboarding" : `/boards/${encodeURIComponent(workflowId)}`);
  const readWorkflow = boardEntry !== undefined ? boardEntry.workflowId : (workflowId ?? null);
  // The tab says what the nav rail says, because both read the one label.
  usePageTitle(label);
  const tenantHasBoard = families.data === undefined ? undefined : boardEntry !== undefined;
  const board = useJobStatusFiles(tenantId, {
    workflowId: readWorkflow,
    includeTerminal,
    enabled: tenantHasBoard === true || families.error !== null,
  });
  const data = board.data;
  const notSetUp = tenantHasBoard === false || isOnboardingNotSetUp(board.error);

  // Walk 3 — "completed files are findable". When the open answer is empty, ask once more WITH
  // finished files (the same cache entry "Show completed files" reads).
  const openFilesEmpty = !includeTerminal && !!data && data.files.length === 0;
  const probe = useJobStatusFiles(tenantId, {
    workflowId: readWorkflow,
    includeTerminal: true,
    enabled: openFilesEmpty,
  });
  const completedFiles = openFilesEmpty ? probe.data?.files.filter((f) => f.is_terminal) : undefined;
  const probeUnresolved = openFilesEmpty && probe.data === undefined && probe.error === null;

  const toggleShow = (choice: ShowChoice): void => {
    const next = show.includes(choice) ? show.filter((c) => c !== choice) : [...show, choice];
    const params = new URLSearchParams(searchParams);
    const serialised = SHOW_ORDER.filter((c) => next.includes(c)).join(",");
    if (serialised === "") params.delete(SHOW_PARAM);
    else params.set(SHOW_PARAM, serialised);
    // `replace`, so a morning of toggling does not bury the page a staffer came from.
    setSearchParams(params, { replace: true });
  };

  // `Date.now()` once per answer, so every age in a paint measures against the SAME instant.
  const now = useMemo(() => Date.now(), [data]);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = (file: JobStatusFile): boolean =>
      needle === "" || (file.client_name ?? "").toLowerCase().includes(needle);
    const out: Record<BoardGroup, BoardRow[]> = {
      "needs-you": [],
      waiting: [],
      completed: [],
      hidden: [],
    };
    // The engine's order is preserved within each group — see the header on why nothing re-sorts.
    for (const file of data?.files ?? []) {
      if (!matches(file)) continue;
      out[groupOf(file)].push({ id: file.job_id, file });
    }
    return out;
  }, [data, query]);

  const filesUnresolved = tenantId !== null && data === undefined && board.error === null;
  const isBusy = identity.isLoading || filesUnresolved || probeUnresolved;
  const failed = identity.error ?? (notSetUp ? null : board.error) ?? null;

  // An empty tenant: a real 200 answer, never an error — and, with finished files not asked for,
  // only once the finished-files probe has also come back empty.
  const isEmptyTenant =
    !!data &&
    data.files.length === 0 &&
    (includeTerminal || (probe.data !== undefined && probe.data.files.length === 0));

  const completedOnly =
    completedFiles !== undefined && completedFiles.length > 0
      ? `${completedFiles.length}${probe.hasMore ? "+" : ""}`
      : null;

  const needsYou = groups["needs-you"];
  // #2507: how many finished files the engine still chases something on.
  const completedOwing = groups.completed.filter((row) => (row.file.owed_count ?? 0) > 0).length;
  const waiting = groups.waiting;
  const waitingOnClient = waiting.filter((row) => row.file.owner === "client").length;
  const searching = query.trim() !== "";

  return (
    <div className="files-board" data-testid="onboarding-board">
      <PageHeader
        title={label}
        subtitle="Every client file, what it is waiting on and what to do next."
        actions={
          notSetUp ? undefined : (
            <>
              <label className="fb-search">
                <Icon name="search" size={16} aria-hidden="true" />
                <input
                  type="search"
                  className="fb-search__input"
                  placeholder="Find a client"
                  aria-label="Find a client"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
              {/* The cross-file chase list — a different QUESTION about the same files. */}
              <Link className="fb-chase" to="/onboarding/outstanding">
                What we are waiting on
              </Link>
              <Button
                variant="ghost"
                onClick={() => void board.refetch()}
                disabled={board.isFetching || !tenantId}
              >
                {board.isFetching ? "Refreshing…" : "Refresh"}
              </Button>
            </>
          )
        }
      />

      {!identity.error && notSetUp && (
        <div className="state" role="status">
          <h2 className="state__title">{notSetUpTitle(identity.tenantName, label)}</h2>
          <p className="state__body">
            There is no {label.toLowerCase()} board for this account yet. Speak to your AR Dept contact if you
            were expecting to see one.
          </p>
        </div>
      )}

      {failed && (
        <div className="state state--error" role="status">
          <h2 className="state__title">We could not load your files</h2>
          <p className="state__body">
            Please try again in a moment. If it keeps happening, contact your AR Dept contact.
          </p>
        </div>
      )}

      {!failed && !notSetUp && isBusy && (
        <div className="fb-loading" aria-busy="true" aria-live="polite">
          <span className="fb-loading__label">Loading your files…</span>
          <div className="skeleton" style={{ width: "35%" }} />
          <div className="skeleton" style={{ width: "80%" }} />
          <div className="skeleton" />
        </div>
      )}

      {!failed && !notSetUp && !isBusy && isEmptyTenant && (
        <EmptyState
          iconName="file-edit"
          title="No onboarding files yet"
          body="As soon as an onboarding is under way it will appear here, with what it is waiting on and what to do next."
        />
      )}

      {!failed && !notSetUp && !isBusy && data && !isEmptyTenant && (
        <>
          <LastUpdated timestamp={new Date(data.as_of)} />

          {completedOnly !== null ? (
            <div className="empty fb-completed-only" role="status">
              <span className="empty__ic">
                <Icon name="list-checks" size={26} />
              </span>
              <h4>No active files.</h4>
              <p>
                <button type="button" className="fb-link" onClick={() => toggleShow("completed")}>
                  {completedOnly} completed →
                </button>
              </p>
            </div>
          ) : (
            <>
              <section className="fb-summary" aria-label="Summary">
                <a className="fb-tile fb-tile--attention" href="#needs-you">
                  <span className="fb-tile__k">Needs you</span>
                  <span className="fb-tile__v num">{needsYou.length}</span>
                  <span className="fb-tile__note">Your team is holding these up</span>
                </a>
                <div className="fb-tile">
                  <span className="fb-tile__k">Waiting on the client</span>
                  <span className="fb-tile__v num">{waitingOnClient}</span>
                  <span className="fb-tile__note">Forms or references still owed</span>
                </div>
                <div className="fb-tile">
                  <span className="fb-tile__k">Waiting on others</span>
                  <span className="fb-tile__v num">{waiting.length - waitingOnClient}</span>
                  <span className="fb-tile__note">People, checks and third parties</span>
                </div>
              </section>

              <section id="needs-you" className="fb-group" aria-labelledby="fb-needs-you-h">
                <h2 id="fb-needs-you-h" className="fb-group__title">
                  Needs you
                </h2>
                {needsYou.length === 0 ? (
                  <p className="fb-group__none">
                    {searching ? "No file that needs you matches your search." : "Nothing needs you right now."}
                  </p>
                ) : (
                  needsYou.map((row) => <NeedsYouRow key={row.id} file={row.file} now={now} base={base} />)
                )}
              </section>

              <section className="fb-group" aria-labelledby="fb-waiting-h">
                <div className="fb-group__head">
                  <h2 id="fb-waiting-h" className="fb-group__title">
                    Waiting on others
                  </h2>
                  <span className="fb-group__note">
                    Nothing for you to do yet. Chase if it is taking too long.
                  </span>
                </div>
                <Table<BoardRow>
                  columns={waitingColumns(now, base)}
                  rows={waiting}
                  caption="Files waiting on others"
                  emptyState={
                    <EmptyState
                      iconName="search-x"
                      title="Nothing to show"
                      body={
                        searching
                          ? "No file waiting on others matches your search."
                          : "No open file is waiting on anyone else."
                      }
                    />
                  }
                />
              </section>

              {showCompleted && (
                <section className="fb-group" aria-labelledby="fb-completed-h">
                  <div className="fb-group__head">
                    <h2 id="fb-completed-h" className="fb-group__title">
                      Completed
                    </h2>
                    {completedOwing > 0 && (
                      <span className="fb-group__note">
                        {completedOwing === 1
                          ? "1 approved file still owes something. Send a reminder from the file."
                          : `${completedOwing} approved files still owe something. Send a reminder from the file.`}
                      </span>
                    )}
                  </div>
                  <Table<BoardRow>
                    columns={completedColumns(now, base)}
                    rows={groups.completed}
                    caption="Completed files"
                    emptyState={
                      <EmptyState
                        iconName="list-checks"
                        title="No completed files"
                        body="Finished files will be listed here."
                      />
                    }
                  />
                </section>
              )}

              {showHidden && (
                <section className="fb-group" aria-labelledby="fb-hidden-h">
                  <h2 id="fb-hidden-h" className="fb-group__title">
                    Cancelled files
                  </h2>
                  <Table<BoardRow>
                    columns={hiddenColumns(base)}
                    rows={groups.hidden}
                    caption="Cancelled files"
                    emptyState={
                      <EmptyState
                        iconName="list-checks"
                        title="No cancelled files"
                        body="There are none to show."
                      />
                    }
                  />
                </section>
              )}
            </>
          )}

          <footer className="fb-footer">
            {/* #2508 (A1): the board pages by cursor. More files than one page are one click away,
                and the button says so rather than the page quietly stopping. */}
            {board.hasMore && (
              <Button variant="ghost" onClick={board.showMore} disabled={board.isLoadingMore}>
                {board.isLoadingMore ? "Loading more…" : "Show more files"}
              </Button>
            )}
            <button type="button" className="fb-link" onClick={() => toggleShow("completed")}>
              {showCompleted ? "Hide completed files" : "Show completed files"}
            </button>
            <button
              type="button"
              className="fb-link fb-link--quiet"
              onClick={() => toggleShow("hidden")}
            >
              {showHidden
                ? "Hide cancelled files"
                : // The count is only honest once finished files were asked for; a cancelled file is
                  // always finished, so before that it would read 0 — left off, not understated.
                  includeTerminal
                  ? `Show cancelled files (${groups.hidden.length})`
                  : "Show cancelled files"}
            </button>
          </footer>
        </>
      )}
    </div>
  );
}

export default FilesBoardPage;
